import * as React from 'react';

import { compose } from 'recompose';

import connectToFluxibleContext from 'js/lib/connectToFluxibleContext';
import connectToRouter from 'js/lib/connectToRouter';
import connectToStores from 'js/lib/connectToStores';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import user from 'js/lib/user';
import waitFor from 'js/lib/waitFor';

import { CenterBox } from '@coursera/coursera-ui';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import { getCurrentSession } from 'bundles/course-sessions/actions/SessionActions';
import SessionStoreClass from 'bundles/course-sessions/stores/SessionStore';
import { loadComputedModels } from 'bundles/course-v2/actions/ComputedModelActions';
import CourseUnauthorized from 'bundles/course-v2/components/CourseUnauthorized';
import ComputedModelStoreClass from 'bundles/course-v2/stores/ComputedModelStore';
import loadCourseBySlug from 'bundles/course-v2/utils/loadCourseBySlug';
import withCourseBySlug from 'bundles/course-v2/utils/withCourseBySlug';
import withCustomLabelsByUserAndCourse from 'bundles/custom-labels/hoc/withCustomLabelsByUserAndCourse';
import {
  loadUserGroupsForCourse,
  loadUserSessionGroupForCourse,
} from 'bundles/group-enroll/actions/GroupSettingActions';
import { loadCertificateData } from 'bundles/in-course-stores/actions/CertificateActions';
import { loadCourseDeadlines } from 'bundles/in-course-stores/actions/CourseDeadlineActions';
import { loadMembership } from 'bundles/in-course-stores/actions/CourseMembershipActions';
import { loadCourseViewGrade } from 'bundles/in-course-stores/actions/CourseViewGradeActions';
import { loadHonorsUserPreferences } from 'bundles/in-course-stores/actions/HonorsUserPreferencesActions';
import { getProgress } from 'bundles/in-course-stores/actions/ProgressActions';
import { loadVerificationDisplay } from 'bundles/in-course-stores/actions/VerificationActions';
import CourseMembershipStoreClass from 'bundles/in-course-stores/stores/CourseMembershipStore';
import CourseStoreClass from 'bundles/in-course-stores/stores/CourseStore';
import CourseViewGradeStoreClass from 'bundles/in-course-stores/stores/CourseViewGradeStore';
import ProgressStoreClass from 'bundles/in-course-stores/stores/ProgressStore';
import S12nStoreClass from 'bundles/in-course-stores/stores/S12nStore';
import VerificationStoreClass from 'bundles/in-course-stores/stores/VerificationStore';

const DataFetcherBody = ({ children }: React.PropsWithChildren) => children;

const DataFetcher = compose<React.PropsWithChildren, React.PropsWithChildren>(
  connectToRouter((router) => ({
    courseSlug: router.params.courseSlug,
    // there is an edge case where for some lab item launch urls we dont want to redirect
    shouldAllowLabPageAccess: router.location.pathname.endsWith('/lab') && !!router.location.query?.programId,
  })),
  withCourseBySlug,
  waitFor(({ isLoadingCourseData }) => {
    return !isLoadingCourseData;
  }),
  connectToFluxibleContext((context: $TSFixMe, props: $TSFixMe) => {
    // Use loadCourseBySlug to handle store setup with the courseData from withCourseBySlug
    context.executeAction(loadCourseBySlug, { courseSlug: props.courseSlug, course: props.courseData });
  }),
  // TODO: connectToStores<Props, InputProps, Stores>
  connectToStores<
    any,
    any,
    SessionStoreClass,
    CourseStoreClass,
    CourseMembershipStoreClass,
    S12nStoreClass,
    VerificationStoreClass,
    CourseViewGradeStoreClass,
    ComputedModelStoreClass,
    ProgressStoreClass
  >(
    [
      SessionStoreClass,
      CourseStoreClass,
      CourseMembershipStoreClass,
      S12nStoreClass,
      VerificationStoreClass,
      CourseViewGradeStoreClass,
      ComputedModelStoreClass,
      ProgressStoreClass,
    ],
    (
      SessionStore,
      CourseStore,
      CourseMembershipStore,
      S12nStore,
      VerificationStore,
      CourseViewGradeStore,
      ComputedModelStore,
      ProgressStore
    ) => {
      return {
        s12n: S12nStore.getS12n(),

        course: CourseStore.getMetadata(),
        courseId: CourseStore.getCourseId(),
        isEnrolled: CourseMembershipStore.isEnrolled(),

        sessionId: SessionStore.getSessionId(),
        isEnrolledInSession: SessionStore.isEnrolled(),

        s12nStoreHasLoaded: S12nStore.hasLoaded(),
        courseStoreHasLoaded: CourseStore.hasLoaded(),
        sessionStoreHasLoaded: SessionStore.hasLoaded(),

        verificationStoreHasLoaded: VerificationStore.hasLoaded(),
        courseMembershipStoreHasLoaded: CourseMembershipStore.hasLoaded(),
        computedModelStoreHasLoaded: ComputedModelStore.hasLoaded(),
        courseIdentifiersHaveLoaded: CourseStore.haveCourseIdentifiersLoaded(),
        courseViewGradeStoreHasLoaded: CourseViewGradeStore.hasLoaded(),
        progressStoreHasLoaded: ProgressStore.hasLoaded(),
      };
    }
  ),
  waitFor(({ courseIdentifiersHaveLoaded }) => courseIdentifiersHaveLoaded),
  waitFor(({ courseId }) => !!courseId, <CourseUnauthorized />),
  connectToFluxibleContext((context: $TSFixMe, { courseId }: $TSFixMe) => {
    context.executeAction(loadMembership, courseId);
  }),
  waitFor(({ courseMembershipStoreHasLoaded }) => courseMembershipStoreHasLoaded),
  waitFor(
    ({ isEnrolled, shouldAllowLabPageAccess }) => user.isSuperuser() || isEnrolled || shouldAllowLabPageAccess,
    <CourseUnauthorized />
  ),
  connectToFluxibleContext((context: $TSFixMe, { courseSlug, courseId }: $TSFixMe) => {
    context.executeAction(loadComputedModels, { courseSlug, courseId });
  }),
  waitFor(({ computedModelStoreHasLoaded }) => computedModelStoreHasLoaded),
  connectToFluxibleContext((context: $TSFixMe, { courseId, courseSlug }: $TSFixMe) => {
    const userId = user.get().id;
    const authenticated = user.isAuthenticatedUser();

    context.executeAction(getCurrentSession, { courseSlug });
    context.executeAction(loadCourseViewGrade, { courseId });

    context.executeAction(loadUserGroupsForCourse, { courseId, userId });
    context.executeAction(loadHonorsUserPreferences, { authenticated });
    context.executeAction(loadCertificateData, { courseId, userId });

    context.executeAction(getProgress, { authenticated, courseId, userId });
  }),
  waitFor(
    ({
      s12nStoreHasLoaded,
      courseStoreHasLoaded,
      sessionStoreHasLoaded,
      courseViewGradeStoreHasLoaded,
      progressStoreHasLoaded,
    }) =>
      s12nStoreHasLoaded &&
      courseStoreHasLoaded &&
      sessionStoreHasLoaded &&
      courseViewGradeStoreHasLoaded &&
      progressStoreHasLoaded
  ),
  connectToFluxibleContext((context: $TSFixMe, { courseId, course, s12n, sessionId }: $TSFixMe) => {
    const userId = user.get().id;
    const authenticated = user.isAuthenticatedUser();

    const s12nId = s12n && s12n.getId();
    const isCourseVerificationEnabled = course.isVerificationEnabled();

    // Loading course schedule depends on knowing whether sessions are enabled or not
    context.executeAction(loadCourseDeadlines, { userId });

    context.executeAction(loadVerificationDisplay, {
      authenticated,
      userId,
      courseId,
      isCourseVerificationEnabled,
      s12nId,
    });

    context.executeAction(loadUserSessionGroupForCourse, { courseId, userId, sessionId });
  }),
  waitFor(({ verificationStoreHasLoaded }) => verificationStoreHasLoaded)
)(DataFetcherBody);

const LegacyDataFetch = ({
  children,
  isLegacyDataLoaded,
}: {
  children?: React.ReactNode;
  isLegacyDataLoaded: boolean;
}) => {
  return (
    <div className="rc-LegacyDataFetch">
      <DataFetcher>{children}</DataFetcher>
      {!isLegacyDataLoaded && (
        <CenterBox height={512}>
          <SvgLoaderSignal />
        </CenterBox>
      )}
    </div>
  );
};

export default compose<React.PropsWithChildren<{ isLegacyDataLoaded: boolean }>, React.PropsWithChildren>(
  deferToClientSideRender,
  withCustomLabelsByUserAndCourse, // call at top level so we avoid flashing in lower level components
  // TODO: connectToStores<Props, InputProps, Stores>
  connectToStores<any, any, VerificationStoreClass>([VerificationStoreClass], (VerificationStore) => ({
    isLegacyDataLoaded: VerificationStore.hasLoaded(),
  }))
)(LegacyDataFetch);
